import * as Rx from 'rxjs';
import { debounceTime, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import type { IPaginationParams, IPaginationResponse } from 'v2/interfaces/pagination';
import { ECustomerSearchTypes, initialFilterValues } from 'v2/interfaces/rating';
import type { IProducerRating, IFilter, IProducerRatingService } from 'v2/interfaces/rating';

import apiService from '../api';
import type { ApiService } from '../api';

const InitialPagination: IPaginationParams = {
  current_page: 1,
  per_page: 10
};

class ProducerRatingService {
  private loadingExporting$ = new Rx.BehaviorSubject<boolean>(false);

  private filters$ = new Rx.BehaviorSubject<IFilter>(initialFilterValues);

  private loadingRatings$ = new Rx.BehaviorSubject<boolean>(true);
  private ratingList$ = new Rx.BehaviorSubject<Array<IProducerRating>>([]);

  private paginationParams$ = new Rx.BehaviorSubject<IPaginationParams>(InitialPagination);
  private paginationResponse$ = new Rx.BehaviorSubject<IPaginationResponse>(null);

  private ordering$ = new Rx.BehaviorSubject<boolean | 'asc' | 'desc'>(false);
  private ordered$ = new Rx.BehaviorSubject<number>(-1);

  private currentProducerRating$ = new Rx.BehaviorSubject<IProducerRating>(null);

  constructor(private apiService: ApiService) {}

  public loadingExporting = () => this.loadingExporting$;

  public setFilter(filter: IFilter): void {
    this.loadingRatings$.next(true);

    this.filters$.pipe(take(1)).subscribe(() => this.filters$.next(filter));
  }

  public getOrdering = (): Rx.Observable<boolean | 'asc' | 'desc'> => this.ordering$;

  public setOrdering(ordering: boolean | 'asc' | 'desc'): void {
    this.ordering$.next(ordering);
  }

  public getOrdered = (): Rx.Observable<number> => this.ordered$;

  public setOrdered(ordered: number): void {
    this.ordered$.next(ordered);
  }

  public getFilters = (): Rx.Observable<IFilter> => this.filters$;

  public getPaginationParams = (): Rx.Observable<IPaginationParams> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<IPaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: IPaginationParams): void {
    this.paginationParams$.next(pagination);
  }

  public resetPage = () => {
    this.paginationParams$.next({ ...this.paginationParams$.value, current_page: 1 });
    return this.getPaginationParams();
  };

  public fixFilter = (filter: Partial<IFilter>): IProducerRatingService => {
    const nFilter: IProducerRatingService = {
      rated_at: filter.date[0],
      rated_at_final: filter.date[1]
    };

    if (filter.customer_type === ECustomerSearchTypes.NAME) {
      nFilter.rater_name = filter.customer;
    }

    if (filter.customer_type === ECustomerSearchTypes.EMAIL) {
      nFilter.rater_email = filter.customer;
    }

    return nFilter;
  };

  public isLoading = () => this.loadingRatings$;

  public getRatings = (): Rx.Observable<Array<IProducerRating>> => this.ratingList$;

  public updateListRatings = () => {
    this.loadingRatings$.next(true);

    this.getFilters()
      .pipe(
        map(f => this.fixFilter(f)),
        switchMap(filters => this.getPaginationParams().pipe(map(params => ({ ...filters, ...params })))),
        switchMap(filters => this.apiService.get('rating/producer', filters)),
        tap(response => this.ratingList$.next(response.data)),
        tap(response => this.paginationResponse$.next(response.pagination)),
        tap(() => this.loadingRatings$.next(false)),
        take(1)
      )
      .subscribe();
  };

  public loadRatings() {
    return this.getFilters().pipe(
      map(f => this.fixFilter(f)),
      switchMap(filters => this.resetPage().pipe(map(params => ({ ...filters, ...params })))),
      debounceTime(10),
      switchMap(filters => {
        this.loadingRatings$.next(true);
        return this.apiService.get('rating/producer', filters).pipe(finalize(() => this.loadingRatings$.next(false)));
      }),
      tap(response => this.ratingList$.next(response.data)),
      tap(response => this.paginationResponse$.next(response.pagination))
    );
  }

  public getCurrentRating = (): Rx.Observable<IProducerRating> => this.currentProducerRating$;

  public setCurrentRating = (producerRating: IProducerRating) => this.currentProducerRating$.next(producerRating);

  public updateRating = (producerRating: Partial<IProducerRating>) =>
    this.apiService.post('rating/producer', { ...producerRating, ...{ date: producerRating.rated_at } });
}

const producerRatingService = new ProducerRatingService(apiService);
export default producerRatingService;
